import type { Component } from '@seek/data-tracking-event-catalogue';
import { useCallback } from 'react';

import { useHubbleClient, type CurrentPage } from './useHubbleClient';

type LoginPromptType = 'popup' | 'linkOut';

export const useLoginPromptTracking = (
  currentPage: CurrentPage,
  currentPageSection:
    | 'page content'
    | 'search filters'
    | 'search results'
    | 'search results mid'
    | 'profile summary',
  loginPromptType?: LoginPromptType,
) => {
  const hubble = useHubbleClient();

  // For auth/login events, append sign-in context based on prompt type
  let authPageSection: string;
  if (loginPromptType === 'linkOut') {
    authPageSection = currentPageSection;
  } else if (loginPromptType === 'popup') {
    authPageSection = `${currentPageSection} sign-in dialog ${loginPromptType}`;
  } else {
    authPageSection = `${currentPageSection} sign-in dialog`;
  }

  const trackAuthLinkPressed = useCallback(
    (eventData: { component: Component }) => {
      hubble.trackAuthLinkPressedEvents({
        currentPage,
        currentPageSection: authPageSection,
        component: eventData.component,
      });
    },
    [hubble, currentPage, authPageSection],
  );

  const trackLoginPressed = useCallback(
    (eventData: { component: Component; identityProvider?: string }) => {
      hubble.trackLoginEvents({
        currentPage,
        currentPageSection: authPageSection,
        component: eventData.component,
        identityProvider: eventData.identityProvider,
      });
    },
    [hubble, currentPage, authPageSection],
  );

  const trackComponentDisplayed = useCallback(
    (eventData: { component: Component }) => {
      hubble.trackComponentDisplayedEvent({
        currentPage,
        currentPageSection,
        component: eventData.component,
      });
    },
    [hubble, currentPage, currentPageSection],
  );

  return {
    trackAuthLinkPressed,
    trackLoginPressed,
    trackComponentDisplayed,
  };
};
